<template>
  <div class="invoices_container multigroup_invoices_container">
    <div class="invoices">
      <div class="title">
        <div>
          <h2>MultiGroup Invoices</h2>
          <small v-if="downlineGroupCount !== null" class="downline_count">
            {{ downlineGroupCount }} downline groups
          </small>
        </div>
        <div>
          <date-picker
            v-model="selectedMonth"
            @input="getInvoices"
            class="invoicePicker"
            type="month"
            :default-value="new Date()"
            placeholder="Select Month"
          ></date-picker>
        </div>
      </div>
      <div class="table_container" style="overflow-x: auto">
        <table>
          <thead>
            <tr>
              <td>Invoice #</td>
              <td>Date</td>
              <td>Totals</td>
              <td>Groups</td>
              <td>Status</td>
              <td>Action</td>
            </tr>
          </thead>
          <tbody v-if="invoices.length">
            <tr v-for="inv in invoices" :key="inv.multi_group_invoice_id">
              <td>
                <strong>{{ inv.invoice_number }}</strong>
              </td>
              <td>
                Invoice: {{ inv.invoice_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                <br />
                Due: {{ inv.invoice_due_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                <br />
                Period:
                {{ inv.invoice_start_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                -
                {{ inv.invoice_end_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
              </td>
              <td>
                Policy Amount: {{ inv.invoice_total | currency }}
                <span>
                  <br />
                  Total Previous Due: {{ inv.invoice_prev_due | currency }}
                </span>
                <br />
                Total Due Amount: {{ inv.invoice_due_amount | currency }}
                <br />
                Total Group Paid Amount: {{ inv.invoice_due_amount_paid | currency }}
                <br />
                Overall Due Amount: {{ inv.invoice_due_amount - inv.invoice_due_amount_paid | currency }}
              </td>
              <td>{{ inv.member_group_count }}</td>
              <td>
                {{ inv.invoice_payment_status }}
                <span v-if="inv.payment_party_status === 'PROCESSING'">
                  <br />
                  Processing
                  <span v-if="inv.processing_date">
                    ({{ inv.processing_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }})
                  </span>
                </span>
              </td>
              <td class="multigroup_actions">
                <div class="icons">
                  <a
                    href="javascript:;"
                    class="action_container"
                    v-tooltip="'List Group Invoices'"
                    @click="openGroupInvoices(inv)"
                  >
                    <span class="action_icon">
                      <i class="fas fa-list-ul"></i>
                    </span>
                    List Group Invoices
                  </a>
                  <a
                    href="javascript:;"
                    class="action_container"
                    v-tooltip="'Payment History'"
                    @click="openPaymentHistory(inv)"
                  >
                    <span class="action_icon">
                      <i class="fas fa-history"></i>
                    </span>
                    Payment History
                  </a>
                  <a
                    href="javascript:;"
                    class="action_container"
                    v-tooltip="'Download Invoice'"
                    @click="downloadInvoice(inv)"
                  >
                    <span class="action_icon">
                      <img src="@/assets/images/download.svg" />
                    </span>
                    Download Invoice
                  </a>
                  <a
                    v-if="inv.can_regenerate"
                    href="javascript:;"
                    class="action_container"
                    v-tooltip="'Regenerate Invoice'"
                    @click="regenerateInvoice(inv)"
                  >
                    <span class="action_icon">
                      <i class="fas fa-sync-alt"></i>
                    </span>
                    Regenerate Invoice
                  </a>
                  <a
                    v-if="inv.can_pay_now"
                    href="javascript:;"
                    class="action_container"
                    v-tooltip="'Pay Now'"
                    @click="payNow(inv)"
                  >
                    <span class="action_icon">
                      <i class="fas fa-credit-card"></i>
                    </span>
                    Pay Now
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <template v-if="!invoices.length && !errorMessage">
          <no-content text="No MultiGroup Invoices Found" />
        </template>
        <p v-if="errorMessage" class="text-danger multigroup_error">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import Api from "@/resource/Api";
import moment from "moment";

export default {
  name: "MultiGroupInvoice",
  data() {
    return {
      invoices: [],
      downlineGroupCount: null,
      selectedMonth: new Date(),
      errorMessage: "",
    };
  },
  methods: {
    errorMessageFrom(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.message) || fallback;
    },
    getInvoices() {
      const month = moment(this.selectedMonth || new Date()).format("YYYY-MM");
      let loader = this.$loading.show();
      this.errorMessage = "";

      Api.get(`multigroup-invoice/list?month=${month}`)
        .then((res) => {
          this.invoices = res.data.data.invoices || [];
          this.downlineGroupCount = res.data.data.downline_group_count;
        })
        .catch(() => {
          this.invoices = [];
          this.errorMessage = "Unable to load multi-group invoices. Please try again later.";
          this.$notify(this.errorMessage, "error");
        })
        .finally(() => {
          loader.hide();
        });
    },
    openGroupInvoices(inv) {
      const route = this.$router.resolve({
        name: "MultiGroupGroupInvoices",
        params: { date: inv.invoice_start_date },
      });
      window.open(route.href, "_blank");
    },
    openPaymentHistory(inv) {
      const route = this.$router.resolve({
        name: "MultiGroupPaymentHistory",
        params: { invoiceId: inv.multi_group_invoice_id },
      });
      window.open(route.href, "_blank");
    },
    // Open the tab synchronously on click so popup blockers allow it, then point it at the URL.
    openPendingTab() {
      return window.open("", "_blank");
    },
    downloadInvoice(inv) {
      let loader = this.$loading.show();
      Api.get(`multigroup-invoice/${inv.multi_group_invoice_id}/download`)
        .then((res) => {
          // The file is served as an attachment, so the dashboard stays on screen.
          window.location.href = res.data.data.file_url;
        })
        .catch((err) => {
          const notFound = err && err.response && err.response.status === 404;
          this.$notify(
            notFound ? "No invoice file is available to download." : "Failed to check the invoice file.",
            "error"
          );
        })
        .finally(() => {
          loader.hide();
        });
    },
    regenerateInvoice(inv) {
      this.$store.commit("setDialogClass", "group-app-confirm-dialog");
      this.$confirm({
        title: "Regenerate Invoice",
        message: "Are you sure you want to regenerate this invoice? This will overwrite the current invoice.",
        button: {
          no: "Cancel",
          yes: "Regenerate",
        },
        callback: (confirm) => {
          if (!confirm) {
            return;
          }
          let loader = this.$loading.show();
          Api.post(`multigroup-invoice/${inv.multi_group_invoice_id}/regenerate`)
            .then((res) => {
              this.$notify(res.data.message || "Invoice regenerated successfully.");
              this.getInvoices();
            })
            .catch((err) => {
              this.$notify(this.errorMessageFrom(err, "Failed to regenerate invoice."), "error");
            })
            .finally(() => {
              loader.hide();
            });
        },
      });
    },
    payNow(inv) {
      const tab = this.openPendingTab();
      let loader = this.$loading.show();
      Api.get(`multigroup-invoice/${inv.multi_group_invoice_id}/pay-now`)
        .then((res) => {
          if (tab) {
            tab.location.href = res.data.data.pay_url;
          } else {
            window.location.href = res.data.data.pay_url;
          }
        })
        .catch((err) => {
          if (tab) tab.close();
          this.$notify(this.errorMessageFrom(err, "Payment is currently unavailable."), "error");
        })
        .finally(() => {
          loader.hide();
        });
    },
  },
  created() {
    this.getInvoices();
  },
};
</script>
<style src="@/assets/css/group_dashboard/invoices.css"></style>
<style scoped>
.multigroup_invoices_container {
  width: 100%;
}
.multigroup_invoices_container .table_container {
  min-height: unset;
  max-height: none;
}
.downline_count {
  color: #254050;
}
.multigroup_actions .action_container {
  white-space: nowrap;
}
.multigroup_actions .action_icon {
  flex-shrink: 0;
}
.multigroup_error {
  padding: 15px 30px;
}
</style>
